import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Shield, ShieldCheck, ShieldAlert, User, Mail, Lock, Unlock, Edit2, X, Save, Loader2, AlertCircle, RefreshCw, LayoutGrid, List, Check, UserCog, Key, Building2, Eye, Download, CheckCircle } from 'lucide-react';
import { userService, permissionService } from '../services';
import type { User as UserType, Role } from '../services';

interface Permission {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
}

// System permission definitions - names/descriptions resolved via i18n at render time
const SYSTEM_PERMISSIONS: Permission[] = [
  { id: 'p1', code: 'canViewDashboard', name: 'permissions.perm.viewDashboard', description: 'permissions.permDesc.viewDashboard', category: 'permissions.category.dashboard' },
  { id: 'p2', code: 'canViewEmployees', name: 'permissions.perm.viewEmployees', description: 'permissions.permDesc.viewEmployees', category: 'permissions.category.employees' },
  { id: 'p3', code: 'canCreateEmployees', name: 'permissions.perm.createEmployees', description: 'permissions.permDesc.createEmployees', category: 'permissions.category.employees' },
  { id: 'p4', code: 'canEditEmployees', name: 'permissions.perm.editEmployees', description: 'permissions.permDesc.editEmployees', category: 'permissions.category.employees' },
  { id: 'p5', code: 'canDeleteEmployees', name: 'permissions.perm.deleteEmployees', description: 'permissions.permDesc.deleteEmployees', category: 'permissions.category.employees' },
  { id: 'p6', code: 'canViewAttendance', name: 'permissions.perm.viewAttendance', description: 'permissions.permDesc.viewAttendance', category: 'permissions.category.attendance' },
  { id: 'p7', code: 'canEditAttendance', name: 'permissions.perm.editAttendance', description: 'permissions.permDesc.editAttendance', category: 'permissions.category.attendance' },
  { id: 'p8', code: 'canApproveAttendance', name: 'permissions.perm.approveAttendance', description: 'permissions.permDesc.approveAttendance', category: 'permissions.category.attendance' },
  { id: 'p9', code: 'canViewLeaves', name: 'permissions.perm.viewLeaves', description: 'permissions.permDesc.viewLeaves', category: 'permissions.category.leaves' },
  { id: 'p10', code: 'canCreateLeaves', name: 'permissions.perm.createLeaves', description: 'permissions.permDesc.createLeaves', category: 'permissions.category.leaves' },
  { id: 'p11', code: 'canApproveLeaves', name: 'permissions.perm.approveLeaves', description: 'permissions.permDesc.approveLeaves', category: 'permissions.category.leaves' },
  { id: 'p12', code: 'canViewDevices', name: 'permissions.perm.viewDevices', description: 'permissions.permDesc.viewDevices', category: 'permissions.category.devices' },
  { id: 'p13', code: 'canManageDevices', name: 'permissions.perm.manageDevices', description: 'permissions.permDesc.manageDevices', category: 'permissions.category.devices' },
  { id: 'p19', code: 'canSyncDevices', name: 'permissions.perm.syncDevices', description: 'permissions.permDesc.syncDevices', category: 'permissions.category.devices' },
  { id: 'p14', code: 'canViewReports', name: 'permissions.perm.viewReports', description: 'permissions.permDesc.viewReports', category: 'permissions.category.reports' },
  { id: 'p15', code: 'canExportReports', name: 'permissions.perm.exportReports', description: 'permissions.permDesc.exportReports', category: 'permissions.category.reports' },
  { id: 'p16', code: 'canManageUsers', name: 'permissions.perm.manageUsers', description: 'permissions.permDesc.manageUsers', category: 'permissions.category.system' },
  { id: 'p17', code: 'canManageRoles', name: 'permissions.perm.manageRoles', description: 'permissions.permDesc.manageRoles', category: 'permissions.category.system' },
  { id: 'p18', code: 'canViewSettings', name: 'permissions.perm.viewSettings', description: 'permissions.permDesc.viewSettings', category: 'permissions.category.system' },
];

// Varsayılan rol izinleri
const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  'Admin': SYSTEM_PERMISSIONS.map(p => p.code), // Tüm izinler
  'Manager': ['canViewDashboard', 'canViewEmployees', 'canEditEmployees', 'canViewAttendance', 'canEditAttendance', 'canApproveAttendance', 'canViewLeaves', 'canApproveLeaves', 'canViewDevices', 'canSyncDevices', 'canViewReports', 'canExportReports'],
  'HR': ['canViewDashboard', 'canViewEmployees', 'canCreateEmployees', 'canEditEmployees', 'canViewAttendance', 'canEditAttendance', 'canViewLeaves', 'canCreateLeaves', 'canApproveLeaves', 'canViewReports', 'canExportReports'],
  'User': ['canViewDashboard', 'canViewLeaves', 'canCreateLeaves'],
  'Employee': ['canViewDashboard', 'canViewLeaves', 'canCreateLeaves'],
};


const Permissions = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [viewMode, setViewMode] = useState<'users' | 'roles'>('users');

  // Her kullanıcının gerçek yetki sayısını tutan map
  const [userPermissionCounts, setUserPermissionCounts] = useState<Record<number, number>>({});

  // Modal states
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersRes, rolesRes] = await Promise.all([
        userService.getAll(),
        userService.getRoles()
      ]);

      if (usersRes.success) {
        setUsers(usersRes.data);

        // Her kullanıcı için yetki sayısını çek
        const counts: Record<number, number> = {};
        await Promise.all(
          usersRes.data.map(async (user) => {
            try {
              const permRes = await permissionService.getUserPermissions(user.id);
              if (permRes.success && permRes.data) {
                counts[user.id] = permRes.data.length;
              } else {
                // Veritabanında yetki yoksa varsayılan rol yetkilerini say
                const defaultPerms = new Set<string>();
                user.roles.forEach(role => {
                  const rolePerms = DEFAULT_ROLE_PERMISSIONS[role.name] || [];
                  rolePerms.forEach(p => defaultPerms.add(p));
                });
                counts[user.id] = defaultPerms.size;
              }
            } catch {
              // Hata durumunda varsayılan rol yetkilerini say
              const defaultPerms = new Set<string>();
              user.roles.forEach(role => {
                const rolePerms = DEFAULT_ROLE_PERMISSIONS[role.name] || [];
                rolePerms.forEach(p => defaultPerms.add(p));
              });
              counts[user.id] = defaultPerms.size;
            }
          })
        );
        setUserPermissionCounts(counts);
      }
      if (rolesRes.success) setRoles(rolesRes.data);
    } catch (err: any) {
      console.error('Load error:', err);
      setError(err.response?.data?.message || t('common.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const searchScope = `${user.username} ${user.email} ${user.fullName || ''}`.toLowerCase();
      const matchesSearch = searchScope.includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === 'all' || user.roles.some(r => r.id === parseInt(selectedRole));
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, selectedRole]);

  const handleEditPermissions = async (user: UserType) => {
    setSelectedUser(user);
    setShowPermissionModal(true);

    try {
      // API'den kullanıcı yetkilerini getir
      const res = await permissionService.getUserPermissions(user.id);
      if (res.success && res.data && res.data.length > 0) {
        setUserPermissions(res.data);
      } else {
        // Veritabanında yetki yoksa, kullanıcının rollerine göre varsayılan izinleri belirle
        const permissions: string[] = [];
        user.roles.forEach(role => {
          const rolePerms = DEFAULT_ROLE_PERMISSIONS[role.name] || [];
          rolePerms.forEach(p => {
            if (!permissions.includes(p)) permissions.push(p);
          });
        });
        setUserPermissions(permissions);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      // Hata durumunda varsayılan izinleri kullan
      const permissions: string[] = [];
      user.roles.forEach(role => {
        const rolePerms = DEFAULT_ROLE_PERMISSIONS[role.name] || [];
        rolePerms.forEach(p => {
          if (!permissions.includes(p)) permissions.push(p);
        });
      });
      setUserPermissions(permissions);
    }
  };

  const togglePermission = (permCode: string) => {
    setUserPermissions(prev =>
      prev.includes(permCode)
        ? prev.filter(p => p !== permCode)
        : [...prev, permCode]
    );
  };

  const savePermissions = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);

      // API'ye kaydet
      const res = await permissionService.saveUserPermissions(selectedUser.id, userPermissions);

      if (res.success) {
        // Yetki sayısını güncelle
        setUserPermissionCounts(prev => ({
          ...prev,
          [selectedUser.id]: userPermissions.length
        }));

        // Başarı mesajı göster
        setSuccessMessage(t('permissions.permissionsSaved', { name: selectedUser.fullName || selectedUser.username }));
        setTimeout(() => setSuccessMessage(null), 3000);

        setShowPermissionModal(false);
        setSelectedUser(null);
      } else {
        setError(t('permissions.saveFailed'));
      }
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || t('permissions.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'manager': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'hr': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getRoleIcon = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin': return <ShieldAlert size={16} className="text-red-600" />;
      case 'manager': return <ShieldCheck size={16} className="text-purple-600" />;
      case 'hr': return <Shield size={16} className="text-blue-600" />;
      default: return <User size={16} className="text-slate-600" />;
    }
  };

  const getPermissionsByCategory = () => {
    const categories: Record<string, Permission[]> = {};
    SYSTEM_PERMISSIONS.forEach(perm => {
      if (!categories[perm.category]) {
        categories[perm.category] = [];
      }
      categories[perm.category].push(perm);
    });
    return categories;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('common.error')}</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadData} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-[100] bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in slide-in-from-top duration-300">
          <CheckCircle size={20} />
          <span className="font-medium">{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('permissions.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('permissions.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium">
            <RefreshCw size={16} /> {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setViewMode('users')}
          className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
            viewMode === 'users' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <UserCog size={16} />
            {t('permissions.userPermissions')}
          </div>
        </button>
        <button
          onClick={() => setViewMode('roles')}
          className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
            viewMode === 'roles' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield size={16} />
            {t('permissions.roleDefinitions')}
          </div>
        </button>
      </div>

      {viewMode === 'users' ? (
        <>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder={t('permissions.searchPlaceholder')}
                className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-sm font-medium transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10 min-w-[150px]"
            >
              <option value="all">{t('permissions.allRoles')}</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>

          {/* Users List */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('permissions.user')}</th>
                  <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.email')}</th>
                  <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('permissions.roles')}</th>
                  <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('permissions.permissionCount')}</th>
                  <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.status')}</th>
                  <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredUsers.map(user => {
                  // Gerçek yetki sayısını kullan (veritabanından veya varsayılan)
                  const permCount = userPermissionCounts[user.id] ?? 0;

                  return (
                    <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-white shadow-sm flex items-center justify-center text-white font-bold text-sm">
                            {(user.firstName?.[0] || user.username[0]).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{user.fullName || user.username}</div>
                            <div className="text-[10px] text-slate-400 font-medium">@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600">{user.email || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map(role => (
                            <span key={role.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${getRoleColor(role.name)}`}>
                              {getRoleIcon(role.name)}
                              {role.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold">
                          <Key size={12} />
                          {permCount} {t('permissions.perms')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                          user.isLocked
                            ? 'bg-red-50 text-red-600 border border-red-100'
                            : user.isActive
                            ? 'bg-green-50 text-green-600 border border-green-100'
                            : 'bg-slate-100 text-slate-400 border border-slate-200'
                        }`}>
                          {user.isLocked ? t('permissions.locked') : user.isActive ? t('employees.active') : t('employees.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEditPermissions(user)}
                            className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title={t('permissions.editPermissions')}
                          >
                            <ShieldCheck size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="py-20 text-center">
              <div className="w-20 h-20 bg-slate-100 rounded-[2rem] flex items-center justify-center mx-auto text-slate-300 mb-6">
                <Search size={40} />
              </div>
              <h3 className="text-lg font-black text-slate-800">{t('permissions.noResults')}</h3>
              <p className="text-slate-500 text-sm font-medium mt-2">{t('permissions.noSearchResults')}</p>
            </div>
          )}
        </>
      ) : (
        /* Roles View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {roles.map(role => {
            const rolePerms = DEFAULT_ROLE_PERMISSIONS[role.name] || [];
            return (
              <div key={role.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-3 rounded-xl ${
                    role.name === 'Admin' ? 'bg-red-100' :
                    role.name === 'Manager' ? 'bg-purple-100' :
                    role.name === 'HR' ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    {getRoleIcon(role.name)}
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800">{role.name}</h3>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">{rolePerms.length} {t('permissions.perms')}</p>
                  </div>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {rolePerms.slice(0, 5).map(permCode => {
                    const perm = SYSTEM_PERMISSIONS.find(p => p.code === permCode);
                    return perm ? (
                      <div key={perm.id} className="flex items-center gap-2 text-xs text-slate-600">
                        <CheckCircle size={12} className="text-green-500" />
                        <span>{t(perm.name)}</span>
                      </div>
                    ) : null;
                  })}
                  {rolePerms.length > 5 && (
                    <div className="text-[10px] text-slate-400 font-bold pt-2">
                      +{rolePerms.length - 5} {t('permissions.morePerms')}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                    {users.filter(u => u.roles.some(r => r.id === role.id)).length} {t('permissions.users')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Permission Edit Modal */}
      {showPermissionModal && selectedUser && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-black text-slate-800">{t('permissions.editPermissions')}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t('permissions.editPermissionsFor', { name: selectedUser.fullName || selectedUser.username })}
                </p>
              </div>
              <button onClick={() => setShowPermissionModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="p-8 overflow-y-auto max-h-[60vh]">
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {selectedUser.roles.map(role => (
                      <span key={role.id} className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold border ${getRoleColor(role.name)}`}>
                        {getRoleIcon(role.name)}
                        {role.name}
                      </span>
                    ))}
                  </div>
                  <span className="text-xs text-blue-600 font-medium">
                    {t('permissions.currentRolesHint')}
                  </span>
                </div>
              </div>

              <div className="space-y-8">
                {Object.entries(getPermissionsByCategory()).map(([category, perms]) => (
                  <div key={category}>
                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      {category.includes('dashboard') && <Eye size={14} />}
                      {category.includes('employees') && <User size={14} />}
                      {category.includes('attendance') && <CheckCircle size={14} />}
                      {category.includes('leaves') && <CheckCircle size={14} />}
                      {category.includes('devices') && <Building2 size={14} />}
                      {category.includes('reports') && <Download size={14} />}
                      {category.includes('system') && <Shield size={14} />}
                      {t(category)}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {perms.map(perm => {
                        const isActive = userPermissions.includes(perm.code);
                        return (
                          <button
                            key={perm.id}
                            type="button"
                            onClick={() => togglePermission(perm.code)}
                            className={`p-4 rounded-xl flex items-center justify-between border transition-all text-left ${
                              isActive
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <div>
                              <div className="text-sm font-bold">{t(perm.name)}</div>
                              <div className="text-[10px] text-slate-500 mt-0.5">{t(perm.description)}</div>
                            </div>
                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                              isActive ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400'
                            }`}>
                              {isActive && <Check size={14} />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-8 py-6 flex gap-4">
              <button
                onClick={() => setShowPermissionModal(false)}
                className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-100 transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={savePermissions}
                disabled={saving}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {saving ? t('common.saving') : t('permissions.savePermissions')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Permissions;
